import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';
import * as React from 'react';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebounce } from '@/hooks/useDebounce';

import { MetadataAsyncMultiSelect } from './MetadataAsyncMultiSelect';
import {
  displayMetadataValue,
  getMetadataFilterLabel,
  getMetadataStaticOptions,
  resolveMetadataFilterWidget,
} from './types';
import { useMetadataValues } from './useMetadataValues';

// Sentinel for the "Any" option in single-select (Radix Select disallows an
// empty-string item value).
const SINGLE_SELECT_ANY_VALUE = '__any__';

interface MetadataFreeTextInputProps {
  value: string;
  onCommit: (value: string) => void;
  placeholder: string;
  className?: string;
}

/**
 * Debounced free-text input for the `free-text` widget. Keeps local keystrokes
 * responsive while only committing (to URL/query state) on a debounce, and
 * re-syncs when the external value changes (reset, back-nav).
 */
const MetadataFreeTextInput: React.FC<MetadataFreeTextInputProps> = ({
  value,
  onCommit,
  placeholder,
  className,
}) => {
  const [draft, setDraft] = React.useState(value);
  const debounced = useDebounce(draft, 350);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  React.useEffect(() => {
    const normalized = debounced.trim();
    if (normalized !== value) {
      onCommit(normalized);
    }
    // onCommit identity is stable per render cycle; guard on value to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <Input
      value={draft}
      onChange={event => setDraft(event.target.value)}
      placeholder={placeholder}
      className={className}
    />
  );
};

interface MetadataFilterControlProps {
  entry: MetadataRegistryEntry;
  /** Currently selected values for this key. */
  selected: string[];
  onChange: (values: string[]) => void;
  organizationId?: string | null;
  programIds?: string[];
  callStartTimeAfter?: string;
  callStartTimeBefore?: string;
  className?: string;
  contentClassName?: string;
}

/**
 * Renders a single metadata advanced-filter control per the widget taxonomy
 * (AQ-496). Selected values are emitted as a string[] via onChange; the parent
 * serializes them to `metadata.<key>=v1,v2`.
 */
export const MetadataFilterControl: React.FC<MetadataFilterControlProps> = ({
  entry,
  selected,
  onChange,
  organizationId,
  programIds,
  callStartTimeAfter,
  callStartTimeBefore,
  className = 'w-[175px] px-3',
  contentClassName = 'w-[18rem]',
}) => {
  const widget = resolveMetadataFilterWidget(entry);
  const label = getMetadataFilterLabel(entry);
  const staticOptions = getMetadataStaticOptions(entry);
  const key = entry.key;

  // Typed search from the multiselect typeahead (stays '' for other widgets).
  const [searchQuery, setSearchQuery] = React.useState('');

  const { values, isLoading } = useMetadataValues({
    organizationId,
    key,
    widget,
    staticOptions,
    programIds,
    callStartTimeAfter,
    callStartTimeBefore,
    q: searchQuery,
  });

  if (widget === 'free-text') {
    return (
      <MetadataFreeTextInput
        value={selected[0] ?? ''}
        onCommit={value => onChange(value ? [value] : [])}
        placeholder={label}
        className={className}
      />
    );
  }

  if (widget === 'single-select') {
    const current = selected[0] ?? '';
    return (
      <Select
        value={current || SINGLE_SELECT_ANY_VALUE}
        onValueChange={value => onChange(value === SINGLE_SELECT_ANY_VALUE ? [] : [value])}
      >
        <SelectTrigger className="w-auto gap-2 [&>span]:flex-none">
          <SelectValue>{current ? displayMetadataValue(current) : label}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SINGLE_SELECT_ANY_VALUE}>Any</SelectItem>
          {values.map(value => (
            <SelectItem key={value} value={value}>
              {displayMetadataValue(value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // multiselect-with-search (default) lists the first 50 values on open and
  // searches the server as you type (AQ-889); async-search-only lists nothing
  // until 2+ chars are typed.
  return (
    <MetadataAsyncMultiSelect
      selected={selected}
      onChange={onChange}
      options={values}
      onQueryChange={setSearchQuery}
      isLoading={isLoading}
      browseWhenEmpty={widget === 'multiselect-with-search'}
      placeholder={label}
      searchPlaceholder={`Search ${label.toLowerCase()}...`}
      className={className}
      contentClassName={contentClassName}
    />
  );
};
